// ═══════════════════════════════════════════════════════════════
// fillVariables.ts – {{KULCS}} változók kitöltése a szervezet adataival betöltött dokumentum-tartalomban
//
// A „Betöltés tartalomként” (DOCX / XLSX / PDF → HTML) után fut: a globális és a szervezeti változók
// helyére a szervezet értéke kerül. A Wordben gyakori, hogy a token több formázási darabra esik
// („{{SZERV” + „_NEV}}”) – a HTML-ben ezért a címkéken átívelő tokent is felismerjük.
// A blokkok ({{BLOKK_…}}), a tartalom-hivatkozások ({{TARTALOM:…}}) és az ismeretlen kulcsok érintetlenek
// maradnak (a megjelenítéskor oldódnak fel, ill. a felhasználó látja, mi maradt kitöltetlen).
// A szinonimák a szerver renderTemplate() szabályával egyeznek (worker/core/template-engine.js).
// ═══════════════════════════════════════════════════════════════

const SYNONYMS: [string, string][] = [
  ['SZERV_NEV', 'ONKORMANYZAT_NEVE'], ['EMAIL', 'SZERVEZET_EMAIL'], ['TELEFON', 'SZERVEZET_TELEFON'],
  ['CIM', 'SZERVEZET_CIME'], ['POSTACIM', 'SZERVEZET_POSTACIM'], ['HONLAP', 'SZERVEZET_HONLAP'],
  ['KRID', 'KRID_PH'], ['KRID', 'KRID_ONK'], ['HIVATALI_KAPU', 'HIVATALI_KAPU_PH'], ['HIVATALI_KAPU', 'HIVATALI_KAPU_ONK'],
];

/** a változók kiegészítve a szinonimákkal és a dátum-változókkal (csak üres helyre) */
export function withSynonyms(vars: Record<string, string>): Record<string, string> {
  const v: Record<string, string> = { AKTUALIS_EV: String(new Date().getFullYear()), AKTUALIS_DATUM: new Date().toISOString().slice(0, 10), ...vars };
  for (const [a, b] of SYNONYMS) {
    if (v[a] && !v[b]) v[b] = v[a];
    else if (v[b] && !v[a]) v[a] = v[b];
  }
  return v;
}

const esc = (s: string) => s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
const SKIP = /^(BLOKK_|TARTALOM:)/;

export interface FillResult { html: string; replaced: string[]; unresolved: string[] }

/**
 * HTML-ben: `{{ KULCS }}` (a kapcsos zárójelek között lehetnek HTML-címkék – azok a kulcsból kiesnek,
 * a csere után a token körüli formázás a nyitó elemé marad).
 */
export function fillVariablesInHtml(html: string, vars: Record<string, string>): FillResult {
  const v = withSynonyms(vars);
  const replaced = new Set<string>();
  const unresolved = new Set<string>();
  const out = String(html || '').replace(/\{((?:<[^>]*>)*)\{((?:<[^>]*>|[^{}<])*?)\}((?:<[^>]*>)*)\}/g, (m, gapOpen: string, inner: string, gapClose: string) => {
    const tags = [...(gapOpen.match(/<[^>]*>/g) || []), ...(inner.match(/<[^>]*>/g) || []), ...(gapClose.match(/<[^>]*>/g) || [])];
    const key = inner.replace(/<[^>]*>/g, '').replace(/&nbsp;|\u00a0/g, ' ').trim();
    if (!/^[A-Za-z0-9_:]{2,80}$/.test(key) || SKIP.test(key)) return m;
    const val = v[key] ?? v[key.toUpperCase()];
    if (val === undefined || val === '') { unresolved.add(key); return m; }
    replaced.add(key);
    // a tokenen belüli címkék (pl. </strong><strong>) megmaradnak, hogy a HTML ép maradjon
    return esc(String(val)) + tags.join('');
  });
  return { html: out, replaced: [...replaced], unresolved: [...unresolved] };
}

/** egyszerű szövegben (pl. PDF-ből kinyert sorok) */
export function fillVariablesInText(text: string, vars: Record<string, string>): FillResult {
  const v = withSynonyms(vars);
  const replaced = new Set<string>();
  const unresolved = new Set<string>();
  const out = String(text || '').replace(/\{\{\s*([A-Za-z0-9_:]{2,80})\s*\}\}/g, (m, key: string) => {
    if (SKIP.test(key)) return m;
    const val = v[key] ?? v[key.toUpperCase()];
    if (val === undefined || val === '') { unresolved.add(key); return m; }
    replaced.add(key);
    return String(val);
  });
  return { html: out, replaced: [...replaced], unresolved: [...unresolved] };
}
